import { MessageBarType } from '@fluentui/react';
import type { ReactNode } from 'react';

// Deep import of the toast module (not the `components-alpha` barrel) so the legacy adapter
// doesn't drag the entire alpha UI surface (Toaster, DateDisplay, Navigation, …) into every
// legacy module that uses `useToast`.
import type { ToastAction, ToastMessage, ToastType } from '@shared/lib/ui/components-alpha/useToast';

import { ToastActions } from '../components/Toast/ToastActions';
import type { IMessageBarMessage } from '../models/components.models';

/**
 * Legacy `addToast` input. Same as `IMessageBarMessage` but `actions` may also be the new-style
 * `ToastAction[]` (rendered as ghost buttons), in addition to the legacy Fluent `ReactNode` slot.
 */
export type LegacyAddToastInput = Omit<IMessageBarMessage, 'actions'> & {
  actions?: ReactNode | ToastAction[];
};

// New-style `ToastAction[]` vs a legacy Fluent `actions` ReactNode (which may itself be an array
// of nodes), distinguished by the `{ label, onClick }` shape.
const isToastActions = (actions: ReactNode | ToastAction[]): actions is ToastAction[] =>
  Array.isArray(actions) && actions.every((a) => a != null && typeof a === 'object' && 'label' in a && 'onClick' in a);

/** Map a Fluent `MessageBarType` to the canonical (new) toast `type`. */
export const toToastType = (messageBarType?: MessageBarType): ToastType => {
  switch (messageBarType) {
    case MessageBarType.error:
    case MessageBarType.blocked:
      return 'error';
    case MessageBarType.warning:
    case MessageBarType.severeWarning:
      return 'warning';
    case MessageBarType.success:
      return 'success';
    case MessageBarType.info:
    default:
      return 'info';
  }
};

/** Map the canonical (new) toast `type` back to a Fluent `MessageBarType`. */
export const toMessageBarType = (type: ToastType): MessageBarType => {
  switch (type) {
    case 'error':
      return MessageBarType.error;
    case 'warning':
      return MessageBarType.warning;
    case 'success':
      return MessageBarType.success;
    case 'info':
    default:
      return MessageBarType.info;
  }
};

/**
 * Convert a legacy toast input into the new context's `addToast` input. The full legacy message is
 * stashed in `meta` so it can be reconstructed faithfully (preserving `messageBarType`, `groupId`,
 * `onClick`, `styles`, … for the legacy UI). New-style `ToastAction[]` are forwarded so the modern
 * `Toaster` renders them too; a legacy `ReactNode` `actions` slot stays in `meta` only.
 */
export const toV2Input = (m: LegacyAddToastInput) => ({
  id: m.id,
  type: toToastType(m.messageBarType),
  message: m.message,
  timeout: m.timeout,
  actions: m.actions !== undefined && isToastActions(m.actions) ? m.actions : undefined,
  meta: m,
});

/** Reconstruct a legacy `IMessageBarMessage` from a unified toast for `MessageBarList`. */
export const toLegacyMessage = (t: ToastMessage): IMessageBarMessage => {
  const base = (t.meta as LegacyAddToastInput | undefined) ?? { message: t.message };
  // New-style `ToastAction[]` → ghost buttons; otherwise fall back to a legacy Fluent `actions` slot.
  let actions: IMessageBarMessage['actions'];
  if (t.actions?.length) {
    actions = <ToastActions actions={t.actions} />;
  } else if (base.actions !== undefined && !isToastActions(base.actions)) {
    actions = base.actions as IMessageBarMessage['actions'];
  }
  return {
    ...(base as IMessageBarMessage),
    id: t.id,
    message: t.message,
    messageBarType: base.messageBarType ?? toMessageBarType(t.type),
    count: t.count,
    actions,
  };
};

/** Read the legacy `groupId` off a unified toast's stashed meta, if any. */
export const getToastGroupId = (t: ToastMessage): string | undefined =>
  (t.meta as IMessageBarMessage | undefined)?.groupId;
